// Only recover deployment-related resource failures, never business/API errors.
const RELOAD_KEY = 'smart:asset-reload-at'
const RELOAD_PARAM = '_smart_reload'
const RELOAD_COOLDOWN = 60 * 1000
let reloading = false

export function isAssetLoadError(event) {
  const error = event && (event.error || event.reason || event)
  if (!error) return false
  return error.name === 'ChunkLoadError' || error.name === 'AssetLoadError' ||
    error.code === 'CSS_CHUNK_LOAD_FAILED' ||
    /Loading (CSS )?chunk [\s\S]*?failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(error.message || '')
}

export function recoverFromAssetError(error, targetUrl) {
  if (process.env.NODE_ENV === 'development' || !isAssetLoadError(error) ||
      reloading || window.navigator.onLine === false) return false

  const currentUrl = new URL(window.location.href)
  // Never repeatedly reload the recovery URL, even if a request times out slowly.
  if (currentUrl.searchParams.has(RELOAD_PARAM)) return false
  const url = new URL(targetUrl || currentUrl.href, currentUrl.href)
  if (url.origin !== currentUrl.origin) return false
  const now = Date.now()
  let lastReload = Number(currentUrl.searchParams.get(RELOAD_PARAM)) || 0
  try {
    lastReload = Math.max(lastReload, Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0)
  } catch (_) {
    // The URL marker also stops loops when sessionStorage is disabled.
  }
  if (lastReload && now - lastReload < RELOAD_COOLDOWN) return false

  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now))
  } catch (_) {}
  reloading = true
  // A new entry URL bypasses HTML cached before the new Nginx policy existed.
  url.searchParams.set(RELOAD_PARAM, String(now))
  window.location.replace(url.href)
  return true
}

export function installAssetRecovery() {
  window.addEventListener('unhandledrejection', recoverFromAssetError)
  window.addEventListener('error', event => {
    const target = event.target
    const source = target && (target.src || (target.rel === 'stylesheet' && target.href))
    if (source) {
      const url = new URL(source, window.location.href)
      if (url.origin === window.location.origin && /\.(m?js|css)$/i.test(url.pathname)) {
        recoverFromAssetError({ name: 'AssetLoadError', message: source })
      }
    } else {
      recoverFromAssetError(event)
    }
  }, true)
}

export async function fetchMicroApp(input, options = {}) {
  const url = new URL(typeof input === 'string' ? input : input.url || String(input), window.location.href)
  const local = url.origin === window.location.origin
  const entry = local && /^\/child\/system-[^/]+\/(?:index\.html)?$/i.test(url.pathname)
  const asset = local && /\.(m?js|css)$/i.test(url.pathname)
  const hashed = /[.-][0-9a-f]{6,}\.(m?js|css)$/i.test(url.pathname)
  const requestOptions = { ...options }
  if (entry) requestOptions.cache = 'no-store'
  else if (asset && !hashed) requestOptions.cache = 'no-cache'

  const response = await window.fetch(input, requestOptions)
  // import-html-entry would otherwise evaluate an HTML fallback as JavaScript.
  if ((entry || asset) && (response.status === 404 || response.status === 410 ||
      (response.ok && asset && /text\/html/i.test(response.headers.get('content-type') || '')))) {
    const error = new Error(url.pathname + ' load failed with status ' + response.status)
    error.name = 'AssetLoadError'
    throw error
  }
  return response
}
